import { mountGoogleMap } from './google.js';
import { mountLeafletMap } from './leaflet.js';

/**
 * Main entry point for rendering the map panel.
 * Dynamically routes to Google Maps or Leaflet based on configuration.
 *
 * @param {HTMLElement} container - The DOM element where the map will be mounted.
 * @param {Object} payload - The data payload containing map points and configuration.
 * @param {Object} ctx - Context object containing translation function (t) and widget configuration (config).
 * @returns {Object} Interface with destroy(), update(), and invalidate() methods.
 */
export function mountMap(container, payload, { t, config }) {
  const apiKey = config?.googleMapsApiKey;
  const cancellationState = { isDestroyed: false };
  let instancePromise;

  // Prefer Google Maps if API key is provided or script is already globally loaded
  const isGoogleMapsInitialized = window.google && window.google.maps && typeof window.google.maps.Map === 'function';
  
  if (apiKey || isGoogleMapsInitialized) {
    instancePromise = mountGoogleMap(container, payload, apiKey, { t, config }, cancellationState);
  } else {
    instancePromise = mountLeafletMap(container, payload, { t, config }, cancellationState);
  }

  return {
    async destroy() {
      cancellationState.isDestroyed = true;
      const instance = await instancePromise;
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    },
    async update(newData) {
      const instance = await instancePromise;
      if (instance && typeof instance.update === 'function') {
        instance.update(newData);
      }
    },
    async invalidate() {
      const instance = await instancePromise;
      if (instance && typeof instance.invalidate === 'function') {
        instance.invalidate();
      }
    }
  };
}
